import React, { useEffect, useMemo, useState } from 'react';
import { useStore, uid } from '../state/store';
import { Card, Banner } from '../components/ui';
import { FinishSetup } from '../components/FinishSetup';
import { TodayCommitted } from '../components/TodayCommitted';
import { PlanOsToday } from '../components/PlanOsToday';
import { CommercialPulse } from '../components/CommercialPulse';
import { AttentionNeeded } from '../components/AttentionNeeded';
import { GateAccountabilityBand } from '../components/GateAccountabilityBand';
import { composeBriefing, applyAccept, recommendationDecision } from '../lib/bridge';
import { fireCrmWriteback } from '../state/api';
import { api } from '../state/api';
import { deriveOperatingMode, founderQueueOk } from '../lib/operatingMode';
import { deriveLaunchPrep } from '../lib/launchPrep';
import { newAction } from '../lib/actions';
import { fmtDate } from '../lib/dates';

/**
 * Founder Today — setup-primary while setup; ready = AI Plan → Constraint → Pulse → Attention.
 * Machinery (plan IDs, fingerprints, validators) lives under More → Diagnostics.
 */
export default function Command({ go, openModal }) {
  const { state, d, patch, add, today } = useStore();
  const [planSoft, setPlanSoft] = useState('');
  const operating = deriveOperatingMode(state, d);
  const isSetup = operating.mode === 'setup';
  const launchPrep = useMemo(
    () => deriveLaunchPrep(state, { today: d.sprint?.start || today, sprintStart: d.sprint?.start }),
    [state, today, d.sprint?.start],
  );

  const lastCloseout = useMemo(() => {
    const rows = [...(state.closeouts || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return rows[0] || null;
  }, [state.closeouts]);

  const briefing = useMemo(
    () => composeBriefing(state, d, { lastCloseout, overrides: state.bridgeRecommendations || [] }),
    [state, d, lastCloseout],
  );

  // Auto-create reversible Bridge-created launch prep move (never evidence / commitment).
  useEffect(() => {
    if (isSetup || !founderQueueOk(state)) return;
    if (!launchPrep.needsPrep || !launchPrep.move) return;
    const existing = (state.top3 || []).some((a) => a.idempotencyKey === launchPrep.move.idempotencyKey);
    if (existing) return;
    add('top3', newAction({ ...launchPrep.move, id: uid('t3') }, today));
  }, [isSetup, state.ui, state.accounts, state.top3, launchPrep, today, add]);

  // Auto-apply routine promote_top3 into AI Plan ranks only (founder pins stay Manual).
  useEffect(() => {
    if (isSetup) return;
    const pending = (briefing.top3 || []).filter((r) => {
      const stored = (state.bridgeRecommendations || []).find((x) => x.id === r.id);
      const status = stored?.status || r.status || 'pending';
      return status === 'pending';
    });
    if (!pending.length) return;
    for (const rec of pending.slice(0, 3)) {
      const actionId = rec.payload?.actionId;
      if (!actionId) continue;
      const action = (state.top3 || []).find((a) => a.id === actionId);
      if (!action || action.status === 'Closed' || action.status === 'Parked') continue;
      if (Number(action.aiPlanRank) > 0 || action.selectedForToday) continue;
      const result = applyAccept(rec, { today, action });
      for (const m of result.mutations || []) {
        if (m.type === 'patch') patch(m.path, m.id, m.changes);
      }
      add(
        'recommendationDecisions',
        recommendationDecision({
          rec,
          status: 'accepted',
          reason: 'Auto-applied deterministic AI Plan rank',
          today,
        }),
      );
      const existing = (state.bridgeRecommendations || []).find((r) => r.id === rec.id);
      if (existing) patch('bridgeRecommendations', rec.id, { status: 'accepted' });
      else add('bridgeRecommendations', { ...rec, date: today, status: 'accepted' });
      if (result.writeback) fireCrmWriteback(result.writeback);
    }
  }, [isSetup, briefing.top3, state.top3, state.bridgeRecommendations, today, patch, add]);

  useEffect(() => {
    let cancelled = false;
    api.planStatus()
      .then((s) => {
        if (cancelled) return;
        if (s?.freshness === 'stale' || s?.status === 'running') {
          setPlanSoft('Plan updating…');
        } else {
          setPlanSoft('');
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [state.planVersions, state.accounts, state.opportunities, state.evidence]);

  const constraintTitle = briefing.constraint?.title || 'Not enough information yet to name the constraint';
  const constraintWhy = briefing.diagnosis || briefing.constraint?.diagnosis || '';
  const preSprint = d.sprint.phase === 'pre';
  const showLaunchBanner = !isSetup && founderQueueOk(state) && preSprint && (launchPrep.priorityCount || 0) > 0;

  if (isSetup) {
    return (
      <>
        {planSoft ? (
          <section className="section">
            <Banner tone="amber">{planSoft}</Banner>
          </section>
        ) : null}
        <FinishSetup go={go} />
        {(state.accounts || []).length > 0 && (
          <section className="section">
            <Card title="While you finish setup">
              <p className="bridge-copy dim">
                Bridge can already draft provisional moves from imported accounts. Confirm or defer your priority list to unlock Today’s full plan.
              </p>
              {constraintWhy ? <p className="dim tiny" style={{ marginTop: 8 }}>{constraintTitle}</p> : null}
            </Card>
          </section>
        )}
      </>
    );
  }

  return (
    <>
      {planSoft ? (
        <section className="section">
          <Banner tone="amber">{planSoft}</Banner>
        </section>
      ) : null}

      {showLaunchBanner ? (
        <section className="section">
          <Card title="Ready for launch">
            <p className="bridge-copy">
              Your {launchPrep.priorityCount}-account priority queue is confirmed. Execution begins{' '}
              {fmtDate(d.sprint.start)}.
            </p>
          </Card>
        </section>
      ) : null}

      <GateAccountabilityBand accountability={d.gateAccountability} go={go} />

      <section className="section">
        <PlanOsToday go={go} />
      </section>

      <TodayCommitted go={go} launchPrep={launchPrep} />

      <section className="section">
        <Card title="Current situation">
          <h2 style={{ fontSize: 20, margin: '0 0 6px', fontWeight: 600 }}>{constraintTitle}</h2>
          {constraintWhy ? <p className="bridge-copy dim">{constraintWhy}</p> : null}
        </Card>
      </section>

      <CommercialPulse go={go} />
      <AttentionNeeded go={go} openModal={openModal} />
    </>
  );
}
